import type {
  CombinedQuotationItems,
  MachineAttachmentItemExtendedDto,
  MachineQuotationItemExtendedDto
} from '../types'
import { calculateMarginNumber } from './calculateMarginNumber'
import { calculateMarginPercentage } from './calculateMarginPercentage'
import { calculateOverAllowanceSplit } from './calculateOverAllowanceSplit'
import { calculateTargetPrice } from './calculateTargetPrice'
import { calculateType } from './calculateType'

export type ItemType =
  | CombinedQuotationItems
  | MachineAttachmentItemExtendedDto
  | MachineQuotationItemExtendedDto

export type FunctionParams = {
  isFirstCalculation?: boolean
  items?: ItemType[] | null
  machineAndAttachmentTotal: number
  overAllowanceTotal: number
  recalculateSellPrice: boolean
  resetSellPrice: boolean
  excludeOverAllowance?: boolean
}

export type CalculateItemPricesParams = {
  isFirstCalculation?: boolean
  item?: ItemType | null
  machineAndAttachmentTotal: number
  overAllowanceTotal: number
  recalculateSellPrice: boolean
  resetSellPrice: boolean
  excludeOverAllowance?: boolean
}

export function calculateMargin(
  margin?: number,
  isFirstCalculation?: boolean
): number {
  const defaultMargin = 1
  const marginValue = margin ?? defaultMargin

  return isFirstCalculation ? marginValue / 100 : marginValue
}

export function getServiceContractFromItem(
  item?: CombinedQuotationItems | null
) {
  return item && 'serviceContract' in item ? item.serviceContract : null
}

export function getSellPrice(
  sellPriceMax: number,
  sellPrice: number,
  recalculateSellPrice: boolean
): number {
  return recalculateSellPrice || sellPrice === 0 ? sellPriceMax : sellPrice
}

export function calculateAndAssignItemPrices({
  isFirstCalculation = true,
  items,
  machineAndAttachmentTotal,
  overAllowanceTotal,
  recalculateSellPrice,
  resetSellPrice = false,
  excludeOverAllowance = false
}: FunctionParams) {
  if (!items) {
    return []
  }

  return items.map(item =>
    calculateItemPrices({
      isFirstCalculation,
      item,
      machineAndAttachmentTotal,
      overAllowanceTotal,
      recalculateSellPrice,
      resetSellPrice,
      excludeOverAllowance
    })
  )
}

export function calculateItemPrices({
  isFirstCalculation,
  item,
  machineAndAttachmentTotal,
  overAllowanceTotal,
  recalculateSellPrice,
  resetSellPrice = false,
  excludeOverAllowance = false
}: CalculateItemPricesParams) {
  const serviceContract = getServiceContractFromItem(item)

  const price =
    Math.round((item?.price ?? 0) - (item?.factorySupport?.dnp ?? 0)) -
    (serviceContract?.dnp ?? 0)

  const overAllowanceSplit = excludeOverAllowance
    ? 0
    : calculateOverAllowanceSplit({
        itemPrice: price,
        machineAndAttachmentTotal: machineAndAttachmentTotal,
        overAllowanceTotal: overAllowanceTotal
      })

  const maxMargin = calculateMargin(
    item?.priceRule?.maxMargin,
    isFirstCalculation
  )
  const minMargin = calculateMargin(
    item?.priceRule?.minMargin,
    isFirstCalculation
  )

  const sellPriceMax = calculateTargetPrice({
    machinePrice: price,
    margin: maxMargin,
    overAllowanceSplit: overAllowanceSplit
  })

  const sellPriceMin = calculateTargetPrice({
    machinePrice: price,
    margin: minMargin,
    overAllowanceSplit: overAllowanceSplit
  })

  //This is the proposed price which is the same as the max sell price (Target Price)
  let sellPrice = getSellPrice(
    sellPriceMax,
    item?.sellPrice ?? 0,
    recalculateSellPrice
  )

  if (resetSellPrice && item?.hasChanged) {
    sellPrice = sellPriceMax
  }

  if (excludeOverAllowance && item?.sellPrice === 0) {
    sellPrice = item?.targetPrice ?? 0
  }

  if (recalculateSellPrice) {
    if (item?.overAllowanceSplit && item.sellPrice) {
      sellPrice = item.sellPrice - item?.overAllowanceSplit
      sellPrice += overAllowanceSplit
    }
  }

  const maxMarginNumber = calculateMarginNumber({
    overAllowanceSplit,
    price: price,
    sellPrice: sellPriceMax
  })

  const minMarginNumber = calculateMarginNumber({
    overAllowanceSplit,
    price: price,
    sellPrice: sellPriceMin
  })

  const proposedMarginNumber = calculateMarginNumber({
    overAllowanceSplit,
    price: price,
    sellPrice: sellPrice
  })

  const proposedPercentage = calculateMarginPercentage({
    machinePrice: price,
    overAllowanceSplit,
    proposedPrice: sellPrice
  })

  const type = calculateType({
    minPrice: minMarginNumber,
    proposedPrice: proposedMarginNumber
  })

  return {
    ...item,
    quotationItemId: item?.quotationItemId ?? '',
    priceRule: {
      ...item?.priceRule,
      maxMargin,
      minMargin
    },
    maxMargin,
    maxMarginNumber: maxMarginNumber,
    minMargin,
    minMarginNumber: minMarginNumber,
    overAllowanceSplit,
    proposedMarginNumber: proposedMarginNumber,
    proposedPercentage,
    sellPrice: sellPrice,
    sellPriceMax: sellPriceMax,
    sellPriceMin: sellPriceMin,
    type
  }
}
